/**
 * `useDispatch` — React hook wrapping `dispatchAction`.
 *
 * Phase 11. Returns a small state machine the form components consume:
 * `{loading, error, result, replayed, run}`. Each `run(input)` mints
 * a fresh `request_id` (locked sub-decision: fresh per click → new
 * audit_log row, not auto-replay).
 *
 * `result` holds the LAST dispatch's response payload on success;
 * `error` holds the `{code, message}` on failure. `replayed` is true
 * iff the backend short-circuited via `idempotency_seen` — used by
 * the page to render a visible badge.
 */

import { useCallback, useState } from "react";
import {
  dispatchAction,
  type DispatchOptions,
  type DispatchResult,
} from "./dispatch";
import { invalidateAfterMutation } from "./resource-cache-invalidation";

export interface UseDispatchState<TOutput> {
  readonly loading: boolean;
  readonly error: { code: string; message: string } | null;
  readonly result: TOutput | null;
  readonly replayed: boolean;
  readonly lastRequestId: string | null;
}

export interface UseDispatchHandle<TInput, TOutput>
  extends UseDispatchState<TOutput> {
  run(input: TInput, options?: DispatchOptions): Promise<DispatchResult<TOutput>>;
  reset(): void;
}

export function useDispatch<TInput, TOutput>(
  actionName: string,
): UseDispatchHandle<TInput, TOutput> {
  const [state, setState] = useState<UseDispatchState<TOutput>>({
    loading: false,
    error: null,
    result: null,
    replayed: false,
    lastRequestId: null,
  });
  const run = useCallback(
    async (
      input: TInput,
      options?: DispatchOptions,
    ): Promise<DispatchResult<TOutput>> => {
      setState({
        loading: true,
        error: null,
        result: null,
        replayed: false,
        lastRequestId: null,
      });
      const r = await dispatchAction<TInput, TOutput>(actionName, input, options);
      if (r.ok) {
        // Phase 67 — a successful write may change any read view, so
        // bust every cached read resource (sub-decision D: over-
        // invalidate when in doubt). The next read of each page
        // refetches instead of serving pre-write data.
        invalidateAfterMutation();
        setState({
          loading: false,
          error: null,
          result: r.response,
          replayed: r.idempotency_replay_short_circuit === true,
          lastRequestId: r.request_id,
        });
      } else {
        setState({
          loading: false,
          error: { code: r.code, message: r.message },
          result: null,
          replayed: false,
          lastRequestId: r.request_id,
        });
      }
      return r;
    },
    [actionName],
  );
  const reset = useCallback((): void => {
    setState({
      loading: false,
      error: null,
      result: null,
      replayed: false,
      lastRequestId: null,
    });
  }, []);
  return { ...state, run, reset };
}
